import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  doc as fsDoc,
} from 'firebase/firestore';
import { FaChevronLeft, FaChevronRight, FaTrophy, FaCalendarAlt, FaClipboardList, FaPlay, FaEye, FaPencilAlt, FaTrash, FaLink, FaUnlink } from 'react-icons/fa';
import ModalPopup from './ModalPopup';
import AddCalendarEventModal from './AddCalendarEventModal';
import ZoneConfigModal from './ZoneConfigModal';
import WorkoutEditorModal from './WorkoutEditorModal';
import WorkoutDetailInline from './WorkoutDetailInline';
import RaceTypeManagerModal from './RaceTypeManagerModal';
import './Calendar.css';
import { buildRaceTypeOptions } from '../utils/raceTypes';
import { cacheKey, getCached, getCachedStale, getCachedStaleAsync, setCached, fingerprint } from '../utils/pageCache';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDate(value) {
  if (!value) return null;

  // Firestore Timestamp
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }

  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) return value;

  return null;
}

function dayKeyFromRun(run) {
  const d =
    toDate(run?.date) ||
    toDate(run?.createdAt) ||
    toDate(run?.timestamp) ||
    toDate(run?.strava?.startDate) ||
    toDate(run?.strava?.startDateLocal);
  if (!d) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function dayKeyFromDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, delta) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function getDaysInMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

function getWeekdayIndex(d) {
  // Monday = 0 ... Sunday = 6 (matches TrainingPeaks-style)
  const js = d.getDay(); // Sunday=0
  return (js + 6) % 7;
}

/** Monday of the week containing d (same week start as getWeekdayIndex). */
function getMonday(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const wd = getWeekdayIndex(date);
  date.setDate(date.getDate() - wd);
  return date;
}

/** List of weeks from start to end (each week = Monday–Sunday). Each item: { weekStart: Date, dayKeys: string[7] }. */
function getWeeksInRange(startMonday, numWeeks) {
  const weeks = [];
  const cursor = new Date(startMonday.getFullYear(), startMonday.getMonth(), startMonday.getDate());
  for (let w = 0; w < numWeeks; w++) {
    const dayKeys = [];
    for (let i = 0; i < 7; i++) {
      dayKeys.push(dayKeyFromDate(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + i)));
    }
    weeks.push({ weekStart: new Date(cursor.getTime()), dayKeys });
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const WEEKS_BACK = 52 * 2;  // ~2 years back
const WEEKS_FORWARD = 52 * 2; // ~2 years forward

const CALENDAR_CACHE_KEY = 'calendar';
const CALENDAR_RUNS_ONLY_KEY = 'calendarRuns'; // fallback when full payload is too large

function readCalendarCache(uid) {
  if (!uid) return null;
  const full = getCached(cacheKey(CALENDAR_CACHE_KEY, uid));
  if (full && Array.isArray(full.runs)) return full;
  const runsOnly = getCached(cacheKey(CALENDAR_RUNS_ONLY_KEY, uid));
  if (runsOnly && Array.isArray(runsOnly.runs))
    return { runs: runsOnly.runs, events: [], plannedWorkouts: [], raceTypes: [] };
  return null;
}

/** Like readCalendarCache but allows expired cache (for immediate display, then revalidate in background). */
function readCalendarCacheStale(uid) {
  if (!uid) return null;
  const full = getCachedStale(cacheKey(CALENDAR_CACHE_KEY, uid));
  if (full && Array.isArray(full.runs)) return full;
  const runsOnly = getCachedStale(cacheKey(CALENDAR_RUNS_ONLY_KEY, uid));
  if (runsOnly && Array.isArray(runsOnly.runs))
    return { runs: runsOnly.runs, events: [], plannedWorkouts: [], raceTypes: [] };
  return null;
}

function writeCalendarCache(uid, payload) {
  const key = cacheKey(CALENDAR_CACHE_KEY, uid);
  const runsKey = cacheKey(CALENDAR_RUNS_ONLY_KEY, uid);
  setCached(key, payload);
  setCached(runsKey, { runs: payload.runs ?? [] });
}

/** Normalize event for display: type (goal -> competition) and competition object. */
function getEventDisplay(ev) {
  if (!ev) return { type: 'event', competition: null };
  const type = ev.type === 'goal' ? 'competition' : (ev.type || 'event');
  const competition = ev.competition || null;
  const priority = competition?.priority;
  const priorityColor =
    priority === 'A' ? '#ff4444' : priority === 'B' ? '#ff8800' : priority === 'C' ? '#ffbb00' : null;
  const raceLabel = competition?.raceTypeName || competition?.raceTypeId || null;
  return { type, competition, priority, priorityColor, raceLabel };
}

/** Fingerprint events including type and competition so updates (e.g. race type, priority) are detected. */
function calendarEventsFingerprint(events) {
  if (!Array.isArray(events)) return '';
  return events
    .map(
      (e) =>
        `${e?.id ?? ''}:${e?.type ?? ''}:${e?.updatedAt ?? 0}:${e?.competition ? `${e.competition.priority ?? ''}-${e.competition.raceTypeId ?? ''}-${e.competition.raceTypeName ?? ''}` : ''}`
    )
    .join('|');
}

/** Multi-fingerprint for calendar payload; uses content-aware fingerprint for events. */
function calendarPayloadFingerprint(runs, events, plannedWorkouts, raceTypes) {
  return [fingerprint(runs), calendarEventsFingerprint(events), fingerprint(plannedWorkouts), fingerprint(raceTypes)].join('|');
}

// One read per mount so all useState initializers see the same cache (stale ok for instant display)
let calendarCacheForInit = { uid: null, data: null };
function getCalendarCacheForInit(uid) {
  if (!uid) return null;
  if (calendarCacheForInit.uid === uid) return calendarCacheForInit.data;
  calendarCacheForInit = { uid, data: readCalendarCacheStale(uid) };
  return calendarCacheForInit.data;
}

function runDurationFromTelemetry(telemetryData) {
  if (!Array.isArray(telemetryData) || telemetryData.length === 0) return null;
  let minT = Infinity;
  let maxT = -Infinity;
  for (let i = 0; i < telemetryData.length; i++) {
    const t = telemetryData[i]?.time;
    if (typeof t === 'number' && Number.isFinite(t)) {
      minT = Math.min(minT, t);
      maxT = Math.max(maxT, t);
    }
  }
  if (minT === Infinity || maxT === -Infinity) return null;
  return Math.max(0, maxT - minT);
}

function RunPopupStats({ run }) {
  if (!run) return null;
  const telemetry = run.telemetryData;
  const durationSec = run.duration != null && Number.isFinite(run.duration)
    ? run.duration
    : runDurationFromTelemetry(telemetry);
  const hasVideo = !!(run.videoUrl || run.youtubeUrl || run.youtube_url || (Array.isArray(run.videos) && run.videos.length > 0));
  const hasTelemetry = !!(Array.isArray(telemetry) && telemetry.length > 0);
  const created = run.createdAt != null
    ? (run.createdAt?.toDate ? run.createdAt.toDate() : new Date(run.createdAt))
    : null;
  const typeLabel = run.sport || run.type || run.runType || null;

  const durationStr =
    durationSec != null
      ? durationSec >= 60
        ? `${Math.floor(durationSec / 60)} min ${Math.round(durationSec % 60)} s`
        : `${Math.round(durationSec)} s`
      : null;

  const blockStyle = { marginTop: 10, color: '#b0b0b0', textAlign: 'left', fontSize: '14px' };
  const rowStyle = { marginBottom: 8 };
  const labelStyle = { color: '#9aa0a6', marginRight: 8 };
  const tagStyle = {
    display: 'inline-block',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    color: '#e0e0e0',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    marginRight: 6,
    marginTop: 4,
  };

  return (
    <div style={blockStyle}>
      {durationStr && (
        <div style={rowStyle}>
          <span style={labelStyle}>Duration</span>
          <span style={{ color: '#e0e0e0' }}>{durationStr}</span>
        </div>
      )}
      {created && (
        <div style={rowStyle}>
          <span style={labelStyle}>Created</span>
          <span style={{ color: '#e0e0e0' }}>{created.toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
        </div>
      )}
      {typeLabel && (
        <div style={rowStyle}>
          <span style={labelStyle}>Type</span>
          <span style={{ color: '#e0e0e0', textTransform: 'capitalize' }}>{String(typeLabel)}</span>
        </div>
      )}
      <div style={{ ...rowStyle, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
        {hasVideo && <span style={tagStyle}>Video</span>}
        {hasTelemetry && <span style={tagStyle}>Telemetry</span>}
      </div>
      <div style={{ ...rowStyle, marginTop: 12, fontSize: 12, color: '#6a6a6a' }}>
        <span style={labelStyle}>ID</span>
        {String(run.id || '')}
      </div>
    </div>
  );
}

const Calendar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const initCache = user ? getCalendarCacheForInit(user.uid) : null;
  const hasCache = initCache && Array.isArray(initCache.runs);
  const [loading, setLoading] = useState(!hasCache);
  const [runs, setRuns] = useState(hasCache ? initCache.runs : []);
  const [events, setEvents] = useState(hasCache ? (initCache.events ?? []) : []);
  const [plannedWorkouts, setPlannedWorkouts] = useState(hasCache ? (initCache.plannedWorkouts ?? []) : []);
  const [raceTypes, setRaceTypes] = useState(hasCache ? (initCache.raceTypes ?? []) : []);
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDayKey, setSelectedDayKey] = useState(() => dayKeyFromDate(new Date()));
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  // Click-to-edit uses AddCalendarEventModal; no separate event-details modal needed.
  const [eventModalState, setEventModalState] = useState({ mode: 'create', event: null });
  const [viewMode, setViewMode] = useState('month'); // month | list
  const [runAction, setRunAction] = useState(null);
  const [runConfirmDelete, setRunConfirmDelete] = useState(false);
  const [dragOverDayKey, setDragOverDayKey] = useState(null);
  const [isZoneConfigOpen, setIsZoneConfigOpen] = useState(false);
  const [isWorkoutEditorOpen, setIsWorkoutEditorOpen] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [workoutViewWorkout, setWorkoutViewWorkout] = useState(null);
  const [isRaceTypeManagerOpen, setIsRaceTypeManagerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const calendarScrollRef = useRef(null);
  const weekRowRefs = useRef(new Map());
  const hasScrolledToToday = useRef(false);

  // Apply cache before paint so first frame shows calendar when cache exists (including stale)
  useLayoutEffect(() => {
    if (!user) return;
    const cached = readCalendarCacheStale(user.uid);
    if (cached && Array.isArray(cached.runs)) {
      setRuns(cached.runs);
      setEvents(cached.events ?? []);
      setPlannedWorkouts(cached.plannedWorkouts ?? []);
      setRaceTypes(cached.raceTypes ?? []);
      setLoading(false);
    } else {
      // Try IndexedDB (async) in case localStorage was cleared
      const key = cacheKey(CALENDAR_CACHE_KEY, user.uid);
      const runsKey = cacheKey(CALENDAR_RUNS_ONLY_KEY, user.uid);
      Promise.all([getCachedStaleAsync(key), getCachedStaleAsync(runsKey)]).then(([full, runsOnly]) => {
        const data = (full && Array.isArray(full.runs)) ? full : (runsOnly && Array.isArray(runsOnly.runs)) ? { runs: runsOnly.runs, events: [], plannedWorkouts: [], raceTypes: [] } : null;
        if (data && Array.isArray(data.runs)) {
          setRuns(data.runs);
          setEvents(data.events ?? []);
          setPlannedWorkouts(data.plannedWorkouts ?? []);
          setRaceTypes(data.raceTypes ?? []);
          setLoading(false);
        }
      });
    }
  }, [user]);

  // Background fetch: load fresh data and update only if different from cache/current state
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const cachedStale = readCalendarCacheStale(user.uid);
    if (!(cachedStale && Array.isArray(cachedStale.runs))) {
      setLoading(true);
    }
    setRefreshing(true);
    Promise.allSettled([fetchRuns(), fetchEvents(), fetchPlannedWorkouts(), fetchRaceTypes()])
      .then((results) => {
        const runsData = results[0].status === 'fulfilled' ? results[0].value : [];
        const eventsData = results[1].status === 'fulfilled' ? results[1].value : [];
        const plannedWorkoutsData = results[2].status === 'fulfilled' ? results[2].value : [];
        const raceTypesData = results[3].status === 'fulfilled' ? results[3].value : [];
        if (results[0].status === 'rejected') console.error('Error loading runs for calendar:', results[0].reason);
        if (results[1].status === 'rejected') console.error('Error loading calendar events:', results[1].reason);
        if (results[2].status === 'rejected') console.error('Error loading planned workouts:', results[2].reason);
        if (results[3].status === 'rejected') console.error('Error loading race types:', results[3].reason);
        const payload = {
          runs: runsData,
          events: eventsData,
          plannedWorkouts: plannedWorkoutsData,
          raceTypes: raceTypesData,
        };
        writeCalendarCache(user.uid, payload);
        const newFp = calendarPayloadFingerprint(runsData, eventsData, plannedWorkoutsData, raceTypesData);
        const prevFp = cachedStale
          ? calendarPayloadFingerprint(
              cachedStale.runs,
              cachedStale.events,
              cachedStale.plannedWorkouts,
              cachedStale.raceTypes
            )
          : '';
        if (newFp !== prevFp) {
          setRuns(runsData);
          setEvents(eventsData);
          setPlannedWorkouts(plannedWorkoutsData);
          setRaceTypes(raceTypesData);
        }
      })
      .catch((err) => {
        console.error('Error loading calendar data:', err);
        alert('Failed to load calendar. Please check the console for details.');
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function fetchRuns() {
    if (!user) return [];
    try {
      try {
        const runsQuery = query(
          collection(db, 'runs'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const runsSnapshot = await getDocs(runsQuery);
        return runsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch {
        const runsQuery = query(collection(db, 'runs'), where('userId', '==', user.uid));
        const runsSnapshot = await getDocs(runsQuery);
        return runsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
    } catch (error) {
      console.error('Error loading runs for calendar:', error);
      throw error;
    }
  }

  async function loadRuns() {
    try {
      setLoading(true);
      const runsData = await fetchRuns();
      setRuns(runsData);
    } catch (error) {
      alert('Failed to load runs for calendar. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchEvents() {
    if (!user) return [];
    try {
      const eventsQuery = query(collection(db, 'events'), where('userId', '==', user.uid));
      const snapshot = await getDocs(eventsQuery);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error loading calendar events:', error);
      return [];
    }
  }

  async function loadEvents() {
    const eventsData = await fetchEvents();
    setEvents(eventsData);
    return eventsData;
  }

  const DRAG_TYPE_EVENT = 'application/x-calendar-event';
  const DRAG_TYPE_WORKOUT = 'application/x-calendar-workout';
  const DRAG_TYPE_RUN = 'application/x-calendar-run';

  async function moveEventToDay(eventId, newDayKey) {
    if (!user || !eventId || !newDayKey) return;
    try {
      await updateDoc(fsDoc(db, 'events', eventId), {
        dayKey: newDayKey,
        updatedAt: Date.now(),
      });
      const freshEvents = await loadEvents();
      if (freshEvents && user) {
        writeCalendarCache(user.uid, {
          runs,
          events: freshEvents,
          plannedWorkouts,
          raceTypes,
        });
      }
    } catch (e) {
      console.error('Failed to move event:', e);
      alert('Failed to move event. Please check the console for details.');
    }
  }

  async function moveWorkoutToDay(workoutId, newDayKey) {
    if (!user || !workoutId || !newDayKey) return;
    const parts = newDayKey.split('-').map(Number);
    if (parts.length !== 3) return;
    const [y, m, d] = parts;
    const newDate = new Date(y, m - 1, d);
    try {
      await updateDoc(fsDoc(db, 'plannedWorkouts', workoutId), {
        scheduledDate: Timestamp.fromDate(newDate),
        updatedAt: Date.now(),
      });
      const freshWorkouts = await fetchPlannedWorkouts();
      setPlannedWorkouts(freshWorkouts);
      if (user) {
        writeCalendarCache(user.uid, {
          runs,
          events,
          plannedWorkouts: freshWorkouts,
          raceTypes,
        });
      }
    } catch (e) {
      console.error('Failed to move workout:', e);
      alert('Failed to move workout. Please check the console for details.');
    }
  }

  async function moveRunToDay(runId, newDayKey) {
    if (!user || !runId || !newDayKey) return;
    const parts = newDayKey.split('-').map(Number);
    if (parts.length !== 3) return;
    const [y, m, d] = parts;
    const newDate = new Date(y, m - 1, d);
    try {
      await updateDoc(fsDoc(db, 'runs', runId), {
        date: Timestamp.fromDate(newDate),
        updatedAt: Date.now(),
      });
      const freshRuns = await fetchRuns();
      setRuns(freshRuns);
      if (user) {
        writeCalendarCache(user.uid, {
          runs: freshRuns,
          events,
          plannedWorkouts,
          raceTypes,
        });
      }
    } catch (e) {
      console.error('Failed to move run:', e);
      alert('Failed to move run. Please check the console for details.');
    }
  }

  function handleDayDragOver(e, dayKey) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDayKey(dayKey);
  }

  function handleDayDrop(e, targetDayKey) {
    e.preventDefault();
    setDragOverDayKey(null);
    if (!targetDayKey) return;
    const eventRaw = e.dataTransfer.getData(DRAG_TYPE_EVENT);
    const workoutRaw = e.dataTransfer.getData(DRAG_TYPE_WORKOUT);
    const runRaw = e.dataTransfer.getData(DRAG_TYPE_RUN);
    try {
      if (eventRaw) {
        const { eventId, sourceDayKey } = JSON.parse(eventRaw);
        if (eventId && targetDayKey !== sourceDayKey) {
          moveEventToDay(eventId, targetDayKey);
        }
      } else if (workoutRaw) {
        const { workoutId, sourceDayKey } = JSON.parse(workoutRaw);
        if (workoutId && targetDayKey !== sourceDayKey) {
          moveWorkoutToDay(workoutId, targetDayKey);
        }
      } else if (runRaw) {
        const { runId, sourceDayKey } = JSON.parse(runRaw);
        if (runId && targetDayKey !== sourceDayKey) {
          moveRunToDay(runId, targetDayKey);
        }
      }
    } catch (err) {
      console.error('Drop data parse error:', err);
    }
  }

  function handleDayDragLeave(e) {
    e.preventDefault();
    setDragOverDayKey(null);
  }

  function handleDragEnd() {
    setDragOverDayKey(null);
  }

  async function fetchPlannedWorkouts() {
    // ShokIQ: no training / planned-workout sync — keep calendar to runs & events only.
    return [];
  }

  async function linkRunToWorkout(workoutId, runId) {
    if (!user || !workoutId || !runId) return;
    try {
      const batch = writeBatch(db);
      const workoutRef = fsDoc(db, 'plannedWorkouts', workoutId);
      const runRef = fsDoc(db, 'runs', runId);
      batch.update(workoutRef, {
        actualWorkoutId: runId,
        status: 'completed',
        completedAt: Timestamp.now(),
        updatedAt: Date.now(),
      });
      batch.update(runRef, {
        plannedWorkoutId: workoutId,
        updatedAt: Date.now(),
      });
      await batch.commit();
      const [freshRuns, freshWorkouts] = await Promise.all([fetchRuns(), fetchPlannedWorkouts()]);
      setRuns(freshRuns);
      setPlannedWorkouts(freshWorkouts);
      if (user) {
        writeCalendarCache(user.uid, {
          runs: freshRuns,
          events,
          plannedWorkouts: freshWorkouts,
          raceTypes,
        });
      }
      setRunAction(null);
    } catch (e) {
      console.error('Failed to link run to workout:', e);
      alert('Failed to link run to workout. Please try again.');
    }
  }

  async function unlinkRunFromWorkout(workoutId, runId) {
    if (!user || !workoutId || !runId) return;
    try {
      const batch = writeBatch(db);
      const workoutRef = fsDoc(db, 'plannedWorkouts', workoutId);
      const runRef = fsDoc(db, 'runs', runId);
      batch.update(workoutRef, {
        actualWorkoutId: null,
        status: 'planned',
        completedAt: null,
        updatedAt: Date.now(),
      });
      batch.update(runRef, {
        plannedWorkoutId: null,
        updatedAt: Date.now(),
      });
      await batch.commit();
      const [freshRuns, freshWorkouts] = await Promise.all([fetchRuns(), fetchPlannedWorkouts()]);
      setRuns(freshRuns);
      setPlannedWorkouts(freshWorkouts);
      if (user) {
        writeCalendarCache(user.uid, {
          runs: freshRuns,
          events,
          plannedWorkouts: freshWorkouts,
          raceTypes,
        });
      }
      setRunAction(null);
      setWorkoutViewWorkout(null);
    } catch (e) {
      console.error('Failed to unlink run from workout:', e);
      alert('Failed to unlink. Please try again.');
    }
  }

  async function loadPlannedWorkouts() {
    const data = await fetchPlannedWorkouts();
    setPlannedWorkouts(data);
  }

  async function fetchRaceTypes() {
    if (!user) return [];
    try {
      const q = query(collection(db, 'raceTypes'), where('userId', '==', user.uid));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error loading race types:', error);
      return [];
    }
  }

  async function loadRaceTypes() {
    const data = await fetchRaceTypes();
    setRaceTypes(data);
  }

  const runsByDay = useMemo(() => {
    const map = new Map();
    for (const run of runs) {
      const key = dayKeyFromRun(run);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(run);
    }

    // Stable sort within day: newest first if possible
    for (const [key, list] of map.entries()) {
      list.sort((a, b) => {
        const da = toDate(a?.date) || toDate(a?.createdAt) || toDate(a?.timestamp);
        const dbb = toDate(b?.date) || toDate(b?.createdAt) || toDate(b?.timestamp);
        const ta = da ? da.getTime() : 0;
        const tb = dbb ? dbb.getTime() : 0;
        return tb - ta;
      });
      map.set(key, list);
    }

    return map;
  }, [runs]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const key = typeof ev?.dayKey === 'string' ? ev.dayKey : null;
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    for (const [key, list] of map.entries()) {
      list.sort((a, b) => {
        const ta = typeof a?.createdAt === 'number' ? a.createdAt : 0;
        const tb = typeof b?.createdAt === 'number' ? b.createdAt : 0;
        return tb - ta;
      });
      map.set(key, list);
    }
    return map;
  }, [events]);

  const workoutsByDay = useMemo(() => {
    const map = new Map();
    for (const workout of plannedWorkouts) {
      if (!workout?.scheduledDate) continue;
      let date;
      if (workout.scheduledDate?.toDate) {
        date = workout.scheduledDate.toDate();
      } else if (workout.scheduledDate instanceof Date) {
        date = workout.scheduledDate;
      } else if (typeof workout.scheduledDate === 'number') {
        date = new Date(workout.scheduledDate);
      } else if (typeof workout.scheduledDate === 'string') {
        date = new Date(workout.scheduledDate);
      } else {
        continue;
      }
      const key = dayKeyFromDate(date);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(workout);
    }
    for (const [key, list] of map.entries()) {
      list.sort((a, b) => {
        const ta = a?.priority === 'p1' ? 3 : a?.priority === 'p2' ? 2 : 1;
        const tb = b?.priority === 'p1' ? 3 : b?.priority === 'p2' ? 2 : 1;
        return tb - ta;
      });
      map.set(key, list);
    }
    return map;
  }, [plannedWorkouts]);

  const monthLabel = useMemo(() => {
    return currentMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }, [currentMonth]);

  /** Start Monday for the scrollable range (WEEKS_BACK before today's week). */
  const scrollRangeStartMonday = useMemo(() => {
    const todayMonday = getMonday(new Date());
    const start = new Date(todayMonday);
    start.setDate(start.getDate() - 7 * WEEKS_BACK);
    return start;
  }, []);

  /** All weeks in the scrollable range (each week has 7 day keys). */
  const weeks = useMemo(() => {
    return getWeeksInRange(scrollRangeStartMonday, WEEKS_BACK + WEEKS_FORWARD);
  }, [scrollRangeStartMonday]);

  const monthKeyPrefix = useMemo(() => {
    return `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-`;
  }, [currentMonth]);

  const monthList = useMemo(() => {
    const dayMap = new Map();

    for (const [dayKey, list] of eventsByDay.entries()) {
      if (!dayKey.startsWith(monthKeyPrefix)) continue;
      dayMap.set(dayKey, { dayKey, events: list, runs: [], workouts: [] });
    }
    for (const [dayKey, list] of runsByDay.entries()) {
      if (!dayKey.startsWith(monthKeyPrefix)) continue;
      if (!dayMap.has(dayKey)) dayMap.set(dayKey, { dayKey, events: [], runs: list, workouts: [] });
      else dayMap.get(dayKey).runs = list;
    }
    for (const [dayKey, list] of workoutsByDay.entries()) {
      if (!dayKey.startsWith(monthKeyPrefix)) continue;
      if (!dayMap.has(dayKey)) dayMap.set(dayKey, { dayKey, events: [], runs: [], workouts: list });
      else dayMap.get(dayKey).workouts = list;
    }

    const out = Array.from(dayMap.values());
    out.sort((a, b) => a.dayKey.localeCompare(b.dayKey));
    return out;
  }, [eventsByDay, monthKeyPrefix, runsByDay, workoutsByDay]);

  const raceTypeOptions = useMemo(() => buildRaceTypeOptions(raceTypes), [raceTypes]);

  const [eventViewEvent, setEventViewEvent] = useState(null);

  function scrollToWeekIndex(index) {
    const el = weekRowRefs.current.get(index);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToMonth(monthDate) {
    const firstDayKey = dayKeyFromDate(startOfMonth(monthDate));
    const idx = weeks.findIndex((w) => w.dayKeys.includes(firstDayKey));
    if (idx >= 0) scrollToWeekIndex(idx);
  }

  function handlePrevMonth() {
    const prev = addMonths(currentMonth, -1);
    setCurrentMonth(prev);
    scrollToMonth(prev);
  }

  function handleNextMonth() {
    const next = addMonths(currentMonth, 1);
    setCurrentMonth(next);
    scrollToMonth(next);
  }

  function handleToday() {
    const today = new Date();
    setCurrentMonth(startOfMonth(today));
    setSelectedDayKey(dayKeyFromDate(today));
    const todayKey = dayKeyFromDate(today);
    const idx = weeks.findIndex((w) => w.dayKeys.includes(todayKey));
    if (idx >= 0) scrollToWeekIndex(idx);
  }

  useEffect(() => {
    if (weeks.length === 0 || hasScrolledToToday.current) return;
    const todayKey = dayKeyFromDate(new Date());
    const idx = weeks.findIndex((w) => w.dayKeys.includes(todayKey));
    if (idx >= 0) {
      hasScrolledToToday.current = true;
      requestAnimationFrame(() => scrollToWeekIndex(idx));
    }
  }, [weeks.length]);

  function handleDayClick(dayKey) {
    setSelectedDayKey(dayKey);
  }

  async function handleDeleteEvent(eventId) {
    if (!user || !eventId) return;
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteDoc(fsDoc(db, 'events', eventId));
      const freshEvents = await loadEvents();
      if (freshEvents && user) {
        writeCalendarCache(user.uid, { runs, events: freshEvents, plannedWorkouts, raceTypes });
      }
    } catch (e) {
      console.error('Failed to delete event:', e);
      alert('Failed to delete event. Please check the console for details.');
    }
  }

  async function handleDeleteWorkout(workoutId) {
    if (!user || !workoutId) return;
    if (!window.confirm('Are you sure you want to delete this workout?')) return;
    try {
      await deleteDoc(fsDoc(db, 'plannedWorkouts', workoutId));
      const freshWorkouts = await fetchPlannedWorkouts();
      setPlannedWorkouts(freshWorkouts);
      if (user) {
        writeCalendarCache(user.uid, { runs, events, plannedWorkouts: freshWorkouts, raceTypes });
      }
    } catch (e) {
      console.error('Failed to delete workout:', e);
      alert('Failed to delete workout. Please check the console for details.');
    }
  }

  async function executeDeleteCalendarRun(runId) {
    if (!user || !runId) return;
    try {
      await deleteDoc(fsDoc(db, 'runs', runId));
      setRunAction(null);
      setRunConfirmDelete(false);
      await loadRuns();
    } catch (e) {
      console.error('Failed to delete run:', e);
      alert('Failed to delete run. Please check the console for details.');
    }
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="calendar-header-row calendar-header-row--top">
          <div className="calendar-title">
            <div className="page-title-with-spinner">
              <h1>Calendar</h1>
              {(loading || refreshing) && <span className="page-title-spinner" aria-hidden="true" title={refreshing ? 'Checking for updates…' : 'Loading…'} />}
            </div>
            <div className="calendar-subtitle">
              {loading ? 'Loading your calendar…' : refreshing ? 'Checking for updates…' : 'Your previous runs & workouts'}
            </div>
          </div>
        </div>

        <div className="calendar-header-row calendar-header-row--controls">
          <div className="calendar-controls">
            <div className="calendar-view-toggle">
              <button
                className={`calendar-btn ${viewMode === 'month' ? 'calendar-btn--active' : ''}`}
                type="button"
                onClick={() => setViewMode('month')}
              >
                Month
              </button>
              <button
                className={`calendar-btn ${viewMode === 'list' ? 'calendar-btn--active' : ''}`}
                type="button"
                onClick={() => setViewMode('list')}
              >
                List
              </button>
            </div>
            <button className="calendar-btn" onClick={handleToday} type="button">
              Today
            </button>
            <button className="calendar-btn icon" onClick={handlePrevMonth} type="button" aria-label="Previous month">
              <FaChevronLeft />
            </button>
            <div className="calendar-month">{monthLabel}</div>
            <button className="calendar-btn icon" onClick={handleNextMonth} type="button" aria-label="Next month">
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="calendar-list">
          {monthList.length === 0 ? (
            <div className="calendar-empty-day">No items this month.</div>
          ) : (
            monthList.map((day) => (
              <div key={day.dayKey} className="calendar-list-day">
                <div className="calendar-list-day-header">
                  <div className="calendar-list-day-title">{day.dayKey}</div>
                  <div className="calendar-list-day-count">
                    {day.events.length + day.workouts.length + day.runs.filter((r) => !day.workouts.some((w) => w.actualWorkoutId === r.id)).length} item
                    {day.events.length + day.workouts.length + day.runs.filter((r) => !day.workouts.some((w) => w.actualWorkoutId === r.id)).length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="calendar-list-items">
                  {day.workouts.map((w) => {
                    const linkedRun = w.actualWorkoutId ? day.runs.find((r) => r.id === w.actualWorkoutId) : null;
                    if (linkedRun) {
                      return (
                        <button
                          key={`linked-${w.id}-${linkedRun.id}`}
                          type="button"
                          className="calendar-list-item calendar-list-item--linked"
                          onClick={() => navigate(`/ride/${linkedRun.id}`)}
                        >
                          <div className="calendar-list-item-title">🔗 {w.name || 'Workout'} + Run</div>
                          <div className="calendar-list-item-meta">
                            <span className="tag">linked</span>
                          </div>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={w.id}
                        type="button"
                        className="calendar-list-item calendar-list-item--draggable"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(DRAG_TYPE_WORKOUT, JSON.stringify({ workoutId: w.id, sourceDayKey: day.dayKey }));
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={handleDragEnd}
                        onClick={() => {
                          setSelectedWorkout(w);
                          setIsWorkoutEditorOpen(true);
                        }}
                      >
                        <div className="calendar-list-item-title">📅 {w.name || 'Planned Workout'}</div>
                        <div className="calendar-list-item-meta">
                          <span
                            className="tag"
                            style={{
                              backgroundColor: w.priority === 'p1' ? '#ff4444' : w.priority === 'p2' ? '#ff8800' : '#ffbb00',
                            }}
                          >
                            {w.priority?.toUpperCase() || 'PLANNED'}
                          </span>
                          {w.targetTSS && <span className="tag">TSS: {w.targetTSS}</span>}
                          {w.type && <span className="tag">{w.type}</span>}
                        </div>
                      </button>
                    );
                  })}
                  {day.events.map((ev) => {
                    const disp = getEventDisplay(ev);
                    return (
                      <div
                        key={ev.id}
                        role="button"
                        tabIndex={0}
                        className="calendar-list-item calendar-list-item--event calendar-list-item--draggable"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(DRAG_TYPE_EVENT, JSON.stringify({ eventId: ev.id, sourceDayKey: day.dayKey }));
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={handleDragEnd}
                        onClick={() => {
                          setEventModalState({ mode: 'edit', event: ev });
                          setIsAddEventOpen(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setEventModalState({ mode: 'edit', event: ev });
                            setIsAddEventOpen(true);
                          }
                        }}
                      >
                        <div className="calendar-list-item-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {disp.priorityColor && (
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: disp.priorityColor,
                                flexShrink: 0,
                              }}
                              aria-hidden
                            />
                          )}
                          {ev.title || 'Event'}
                        </div>
                        <div className="calendar-list-item-meta">
                          <span className="tag">{disp.type}</span>
                          {disp.raceLabel ? (
                            <span className="tag">Race: {disp.raceLabel}</span>
                          ) : null}
                          {disp.priority && (disp.priority === 'A' || disp.priority === 'B' || disp.priority === 'C') ? (
                            <span
                              className="tag"
                              style={{
                                backgroundColor: disp.priorityColor,
                                borderColor: disp.priorityColor,
                                color: '#fff',
                              }}
                            >
                              {disp.priority} race
                            </span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                  {(day.runs.filter((r) => !day.workouts.some((w) => w.actualWorkoutId === r.id))).map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="calendar-list-item"
                      onClick={() => setRunAction(r)}
                    >
                      <div className="calendar-list-item-title">{r.name || r.runName || 'Run'}</div>
                      <div className="calendar-list-item-meta">
                        <span className="tag">run</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="calendar-weeks-wrap" ref={calendarScrollRef}>
          <div className="calendar-weeks-header">
            {WEEKDAYS.map((w) => (
              <div key={w} className="calendar-weekday">{w}</div>
            ))}
          </div>
          <div className="calendar-weeks">
            {weeks.map((week, weekIndex) => {
              return (
                <div
                  key={week.weekStart.getTime()}
                  className="calendar-week-row"
                  ref={(el) => {
                    if (el) weekRowRefs.current.set(weekIndex, el);
                  }}
                >
                  {week.dayKeys.map((dayKey) => {
                    const dayRuns = runsByDay.get(dayKey) ?? [];
                    const dayEvents = eventsByDay.get(dayKey) ?? [];
                    const dayWorkouts = workoutsByDay.get(dayKey) ?? [];
                    const linkedRunIds = new Set(dayWorkouts.filter((w) => w.actualWorkoutId).map((w) => w.actualWorkoutId));
                    const unlinkedRuns = dayRuns.filter((r) => !linkedRunIds.has(r.id));
                    const totalItems = dayEvents.length + dayWorkouts.length + unlinkedRuns.length;
                    const sameDayWorkouts = dayWorkouts.filter((w) => !w.actualWorkoutId);
                    const isSelected = dayKey === selectedDayKey;
                    const isToday = dayKey === dayKeyFromDate(new Date());
                    const parts = dayKey.split('-').map(Number);
                    const dayNumber = parts.length === 3 ? parts[2] : '';
                    const cellDate = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : null;
                    const todayMidnight = new Date();
                    todayMidnight.setHours(0, 0, 0, 0);
                    const isFuture = cellDate && cellDate.getTime() > todayMidnight.getTime();

                    return (
                      <div
                        key={dayKey}
                        role="button"
                        tabIndex={0}
                        className={`calendar-cell day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''} ${dragOverDayKey === dayKey ? 'calendar-cell--drag-over' : ''}`}
                        onClick={(e) => { if (!e.target.closest('.calendar-day-event-item')) handleDayClick(dayKey); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!e.target.closest('.calendar-day-event-item')) handleDayClick(dayKey); } }}
                        onDragOver={(e) => handleDayDragOver(e, dayKey)}
                        onDrop={(e) => handleDayDrop(e, dayKey)}
                        onDragLeave={handleDayDragLeave}
                        title={totalItems ? `${totalItems} item(s)` : ''}
                      >
                        <div className="calendar-day-number">{dayNumber}</div>
                        {totalItems > 0 && (
                          <div className="calendar-day-events">
                            {dayWorkouts.map((w) => {
                              const linkedRun = w.actualWorkoutId ? dayRuns.find((r) => r.id === w.actualWorkoutId) : null;
                              if (linkedRun) {
                                return (
                                  <div
                                    key={`linked-${w.id}-${linkedRun.id}`}
                                    className="calendar-day-event-item calendar-day-event-item--linked"
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/ride/${linkedRun.id}`); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); navigate(`/ride/${linkedRun.id}`); } }}
                                    title="Workout + Run (click to open)"
                                  >
                                    <span className="calendar-day-event-item-icon" aria-hidden><FaLink /></span>
                                    <span className="calendar-day-event-item-label">{w.name || 'Workout'}</span>
                                    <span className="calendar-day-event-item-sublabel"> + Run</span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={w.id}
                                  className="calendar-day-event-item calendar-day-event-item--workout calendar-day-event--draggable calendar-day-event--drop-target"
                                  role="button"
                                  tabIndex={0}
                                  draggable
                                  onDragStart={(e) => {
                                    e.stopPropagation();
                                    e.dataTransfer.setData(DRAG_TYPE_WORKOUT, JSON.stringify({ workoutId: w.id, sourceDayKey: dayKey }));
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragEnd={handleDragEnd}
                                  onDragOver={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const runRaw = e.dataTransfer.getData(DRAG_TYPE_RUN);
                                    if (runRaw) e.dataTransfer.dropEffect = 'link';
                                  }}
                                  onDrop={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const runRaw = e.dataTransfer.getData(DRAG_TYPE_RUN);
                                    if (runRaw) {
                                      try {
                                        const { runId } = JSON.parse(runRaw);
                                        if (runId && w.id) linkRunToWorkout(w.id, runId);
                                      } catch (err) {
                                        console.error('Drop parse error:', err);
                                      }
                                    }
                                  }}
                                  onClick={(e) => { e.stopPropagation(); setWorkoutViewWorkout(w); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setWorkoutViewWorkout(w); } }}
                                  title={sameDayWorkouts.length ? 'Drag a run here to associate' : undefined}
                                >
                                  <span className="calendar-day-event-item-icon" aria-hidden><FaClipboardList /></span>
                                  <span className="calendar-day-event-item-label">{w.name || 'Planned Workout'}</span>
                                </div>
                              );
                            })}
                            {dayEvents.map((ev) => {
                              const disp = getEventDisplay(ev);
                              const isRace = disp.type === 'competition';
                              return (
                                <div
                                  key={ev.id}
                                  className="calendar-day-event-item calendar-day-event-item--event calendar-day-event--draggable"
                                  role="button"
                                  tabIndex={0}
                                  draggable
                                  onDragStart={(e) => {
                                    e.stopPropagation();
                                    e.dataTransfer.setData(DRAG_TYPE_EVENT, JSON.stringify({ eventId: ev.id, sourceDayKey: dayKey }));
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragEnd={handleDragEnd}
                                  onClick={(e) => { e.stopPropagation(); setEventViewEvent(ev); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setEventViewEvent(ev); } }}
                                >
                                  <span className="calendar-day-event-item-icon" aria-hidden>{isRace ? <FaTrophy /> : <FaCalendarAlt />}</span>
                                  <span className="calendar-day-event-item-label">{ev.title || 'Event'}</span>
                                </div>
                              );
                            })}
                            {unlinkedRuns.map((r) => (
                              <div
                                key={r.id}
                                className="calendar-day-event-item calendar-day-event-item--ride"
                                role="button"
                                tabIndex={0}
                                draggable
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  e.dataTransfer.setData(DRAG_TYPE_RUN, JSON.stringify({ runId: r.id, sourceDayKey: dayKey }));
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={handleDragEnd}
                                onClick={(e) => { e.stopPropagation(); setRunAction(r); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setRunAction(r); } }}
                              >
                                <span className="calendar-day-event-item-icon" aria-hidden><FaPlay /></span>
                                <span className="calendar-day-event-item-label">{r.name || r.runName || 'Run'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AddCalendarEventModal
        isOpen={isAddEventOpen}
        onClose={() => {
          setIsAddEventOpen(false);
          setEventModalState({ mode: 'create', event: null });
        }}
        defaultDayKey={selectedDayKey}
        busy={savingEvent}
        mode={eventModalState.mode}
        initialEvent={eventModalState.event}
        raceTypeOptions={raceTypeOptions}
        onManageRaceTypes={() => setIsRaceTypeManagerOpen(true)}
        onDelete={async (eventId) => {
          if (!user || !eventId) return false;
          const ok = window.confirm('Delete this event?');
          if (!ok) return false;
          try {
            await deleteDoc(fsDoc(db, 'events', eventId));
            const freshEvents = await loadEvents();
            if (freshEvents && user) {
              writeCalendarCache(user.uid, {
                runs,
                events: freshEvents,
                plannedWorkouts,
                raceTypes,
              });
            }
            return true;
          } catch (e) {
            console.error('Failed to delete event:', e);
            alert('Failed to delete event. Please check the console for details.');
            return false;
          }
        }}
        onSubmit={async ({ id, dayKey, type, title, notes, competition }) => {
          if (!user) return false;
          try {
            setSavingEvent(true);
            if (eventModalState.mode === 'edit' && id) {
              await updateDoc(fsDoc(db, 'events', id), {
                dayKey,
                type,
                title,
                notes: notes || '',
                competition: competition || null,
                updatedAt: Date.now(),
              });
            } else {
              await addDoc(collection(db, 'events'), {
                userId: user.uid,
                dayKey,
                type,
                title,
                notes: notes || '',
                competition: competition || null,
                createdAt: Date.now(),
              });
            }
            const freshEvents = await loadEvents();
            if (freshEvents && user) {
              writeCalendarCache(user.uid, {
                runs,
                events: freshEvents,
                plannedWorkouts,
                raceTypes,
              });
            }
            return true;
          } catch (e) {
            console.error('Failed to save calendar event:', e);
            alert('Failed to save event. Please check the console for details.');
            return false;
          } finally {
            setSavingEvent(false);
          }
        }}
      />

      <ModalPopup
        isOpen={!!runAction}
        title={
          <span className="calendar-popup-title-with-icon">
            <span className="calendar-popup-title-icon calendar-popup-title-icon--ride" aria-hidden><FaPlay /></span>
            {runAction?.name || runAction?.runName || 'Run'}
          </span>
        }
        message=""
        onClose={() => { setRunAction(null); setRunConfirmDelete(false); }}
        showFooter={false}
      >
        {runAction ? (
          <>
            <div style={{ textAlign: 'left' }}>
              {runAction.plannedWorkoutId && (() => {
                const linkedWorkout = plannedWorkouts.find((w) => w.id === runAction.plannedWorkoutId);
                return linkedWorkout ? (
                  <div className="calendar-run-linked-badge" style={{ marginBottom: 12, padding: 8, background: 'rgba(0,120,200,0.15)', borderRadius: 8 }}>
                    <span className="calendar-run-linked-label"><FaLink /> Linked to workout: {linkedWorkout.name || 'Workout'}</span>
                  </div>
                ) : null;
              })()}
              {!runAction.plannedWorkoutId && (() => {
                const runDayKey = dayKeyFromRun(runAction);
                const sameDayWorkoutsForAssociate = (runDayKey ? (workoutsByDay.get(runDayKey) ?? []) : []).filter((w) => !w.actualWorkoutId);
                if (sameDayWorkoutsForAssociate.length === 0) return null;
                return (
                  <div className="calendar-run-associate-prompt" style={{ marginBottom: 12, padding: 8, background: 'rgba(80,80,80,0.2)', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, color: '#9aa0a6', marginBottom: 6 }}>Same day workout(s) — associate this run:</div>
                    {sameDayWorkoutsForAssociate.map((w) => (
                      <button
                        key={w.id}
                        type="button"
                        className="calendar-view-popup-btn calendar-view-popup-btn--primary"
                        style={{ marginRight: 8, marginBottom: 4 }}
                        onClick={() => linkRunToWorkout(w.id, runAction.id)}
                      >
                        <FaLink /> {w.name || 'Workout'}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <RunPopupStats run={runAction} />
            </div>
            <div className="calendar-view-popup-actions">
              {runAction.plannedWorkoutId && (() => {
                const linkedWorkout = plannedWorkouts.find((w) => w.id === runAction.plannedWorkoutId);
                return linkedWorkout ? (
                  <button
                    type="button"
                    className="calendar-view-popup-btn calendar-view-popup-btn--secondary"
                    onClick={() => unlinkRunFromWorkout(linkedWorkout.id, runAction.id)}
                  >
                    <FaUnlink /> Unlink from workout
                  </button>
                ) : null;
              })()}
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--delete"
                onClick={() => setRunConfirmDelete(true)}
              >
                <FaTrash /> Delete
              </button>
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--primary"
                onClick={() => { navigate(`/ride/${runAction.id}`); setRunAction(null); setRunConfirmDelete(false); }}
              >
                <FaPencilAlt /> Edit
              </button>
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--ok"
                onClick={() => { setRunAction(null); setRunConfirmDelete(false); }}
              >
                OK
              </button>
            </div>
          </>
        ) : null}
      </ModalPopup>

      <ModalPopup
        isOpen={!!runAction && runConfirmDelete}
        title="Delete this run?"
        message="This removes the run from the cloud and your calendar. This cannot be undone."
        onClose={() => setRunConfirmDelete(false)}
        onCancel={() => setRunConfirmDelete(false)}
        cancelLabel="Cancel"
        onConfirm={() => {
          if (runAction?.id) executeDeleteCalendarRun(runAction.id);
        }}
        confirmLabel="Delete"
      />

      <ModalPopup
        isOpen={!!workoutViewWorkout}
        title={
          <span className="calendar-popup-title-with-icon">
            <span className="calendar-popup-title-icon calendar-popup-title-icon--workout" aria-hidden><FaClipboardList /></span>
            {workoutViewWorkout?.name || 'Workout'}
          </span>
        }
        message=""
        onClose={() => setWorkoutViewWorkout(null)}
        showFooter={false}
      >
        {workoutViewWorkout ? (
          <>
            <div style={{ textAlign: 'left', marginTop: 0 }}>
              <WorkoutDetailInline workout={workoutViewWorkout} />
            </div>
            <div className="calendar-view-popup-actions">
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--delete"
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to delete this workout?')) return;
                  await handleDeleteWorkout(workoutViewWorkout.id);
                  setWorkoutViewWorkout(null);
                }}
              >
                <FaTrash /> Delete
              </button>
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--primary"
                onClick={() => {
                  setSelectedWorkout(workoutViewWorkout);
                  setWorkoutViewWorkout(null);
                  setIsWorkoutEditorOpen(true);
                }}
              >
                <FaPencilAlt /> Edit
              </button>
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--ok"
                onClick={() => setWorkoutViewWorkout(null)}
              >
                OK
              </button>
            </div>
          </>
        ) : null}
      </ModalPopup>

      <ModalPopup
        isOpen={!!eventViewEvent}
        title={
          eventViewEvent ? (
            <span className="calendar-popup-title-with-icon">
              <span className={`calendar-popup-title-icon ${getEventDisplay(eventViewEvent).type === 'competition' ? 'calendar-popup-title-icon--race' : 'calendar-popup-title-icon--event'}`} aria-hidden>
                {getEventDisplay(eventViewEvent).type === 'competition' ? <FaTrophy /> : <FaCalendarAlt />}
              </span>
              {eventViewEvent.title || 'Event'}
            </span>
          ) : 'Event'
        }
        message=""
        onClose={() => setEventViewEvent(null)}
        showFooter={false}
      >
        {eventViewEvent ? (
          <>
            <div className="calendar-event-view-body" style={{ textAlign: 'left' }}>
              {(() => {
                const disp = getEventDisplay(eventViewEvent);
                const isRace = disp.type === 'competition';
                return (
                  <>
                    <div className="calendar-event-view-meta">
                      <span className="calendar-event-view-meta-item">
                        <span className="calendar-event-view-meta-label">Race type</span>
                        <span className="calendar-event-view-meta-value">{disp.raceLabel || '—'}</span>
                      </span>
                      <span className="calendar-event-view-meta-item">
                        <span className="calendar-event-view-meta-label">Race priority</span>
                        <span className="calendar-event-view-meta-value">
                          {disp.priority && ['A','B','C'].includes(disp.priority) ? disp.priority : '—'}
                        </span>
                      </span>
                    </div>
                    {eventViewEvent.notes && (
                      <p className="calendar-event-view-notes">{eventViewEvent.notes}</p>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="calendar-view-popup-actions">
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--delete"
                onClick={async () => {
                  await handleDeleteEvent(eventViewEvent.id);
                  setEventViewEvent(null);
                }}
              >
                <FaTrash /> Delete
              </button>
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--primary"
                onClick={() => {
                  setEventModalState({ mode: 'edit', event: eventViewEvent });
                  setEventViewEvent(null);
                  setIsAddEventOpen(true);
                }}
              >
                <FaPencilAlt /> Edit
              </button>
              <button
                type="button"
                className="calendar-view-popup-btn calendar-view-popup-btn--ok"
                onClick={() => setEventViewEvent(null)}
              >
                OK
              </button>
            </div>
          </>
        ) : null}
      </ModalPopup>

      <ZoneConfigModal
        isOpen={isZoneConfigOpen}
        onClose={() => setIsZoneConfigOpen(false)}
        userId={user?.uid}
      />

      <WorkoutEditorModal
        isOpen={isWorkoutEditorOpen}
        onClose={(refresh) => {
          setIsWorkoutEditorOpen(false);
          setSelectedWorkout(null);
          if (refresh) {
            loadPlannedWorkouts();
          }
        }}
        userId={user?.uid}
        workout={selectedWorkout}
        mode={selectedWorkout ? 'edit' : 'create'}
        planId={selectedWorkout?.planId}
      />

      <RaceTypeManagerModal
        isOpen={isRaceTypeManagerOpen}
        onClose={() => setIsRaceTypeManagerOpen(false)}
        userId={user?.uid}
        onChanged={() => loadRaceTypes()}
      />
    </div>
  );
};

export default Calendar;

